// Legal / compliance content for DSA eCommerce funnel.
// NOTE: These are professionally-worded templates and should be reviewed by
// qualified legal counsel before going live. Wording follows FTC guidance:
// no income guarantees, clear "results not typical" language, and disclosures.

const COMPANY = "DSA eCommerce";
const CONTACT = "support@dsaecommerce.com";
const UPDATED = "Updated: 2026";

export const LEGAL = {
  terms: {
    title: "Terms & Conditions",
    updated: UPDATED,
    intro: `These Terms & Conditions ("Terms") govern your use of the ${COMPANY} website and your engagement of our store-management services. By accessing this site or booking a strategy call, you agree to these Terms.`,
    sections: [
      {
        h: "1. Our Services",
        p: [
          `${COMPANY} provides done-for-you eCommerce store setup and management services for Amazon, Walmart, and eBay marketplaces, which may include product research, listing creation, advertising, account and compliance management, and fulfillment coordination.`,
          "The specific scope, fees, and deliverables of any engagement are defined in a separate written service agreement provided during onboarding. This website is marketing material and does not by itself create a service contract.",
        ],
      },
      {
        h: "2. No Guarantee of Results",
        p: [
          "We do not promise or guarantee any specific level of sales, revenue, income, or profit. Marketplace performance depends on many factors, including but not limited to product selection, capital invested, competition, seasonality, account health, and platform policies — many of which are outside our control.",
          "Any figures, case studies, or examples shown are illustrative of individual outcomes and are not a promise that you will achieve similar results.",
        ],
      },
      {
        h: "3. Client Responsibilities",
        p: [
          "You are responsible for providing accurate business information, maintaining your marketplace accounts in good standing, funding inventory and advertising as agreed, and complying with all applicable marketplace policies and laws.",
          "You retain full ownership of your store, brand, and accounts at all times. We act solely as your operations partner and never as a reseller or owner of your business.",
        ],
      },
      {
        h: "4. Fees",
        p: [
          "Fees are set out in your service agreement. Please review your agreement carefully before enrolling and contact us with any questions about billing.",
        ],
      },
      {
        h: "5. Intellectual Property",
        p: [
          `All content on this website, including text, graphics, logos, and the ${COMPANY} name, is owned by or licensed to ${COMPANY} and may not be reproduced without permission. Third-party brand marks shown are the property of their respective owners and are used for identification purposes only.`,
        ],
      },
      {
        h: "6. Limitation of Liability",
        p: [
          `To the maximum extent permitted by law, ${COMPANY} shall not be liable for any indirect, incidental, or consequential damages, or for lost profits, arising from your use of this website or our services.`,
        ],
      },
      {
        h: "7. Changes & Contact",
        p: [
          `We may update these Terms from time to time. Continued use of the site constitutes acceptance of the current Terms. Questions? Contact us at ${CONTACT}.`,
        ],
      },
    ],
  },

  privacy: {
    title: "Privacy Policy",
    updated: UPDATED,
    intro: `${COMPANY} respects your privacy. This Privacy Policy explains what information we collect, how we use it, and your choices.`,
    sections: [
      {
        h: "1. Information We Collect",
        p: [
          "Information you provide: name, email, phone number, business details, and anything you submit when you book a strategy call or apply.",
          "Information collected automatically: device, browser, IP address, and usage data via cookies and similar technologies for analytics and site functionality.",
        ],
      },
      {
        h: "2. How We Use Information",
        p: [
          "To respond to inquiries, schedule and conduct strategy calls, provide and improve our services, communicate with you, and for security, analytics, and legal compliance.",
        ],
      },
      {
        h: "3. Sharing",
        p: [
          "We do not sell your personal information. We may share it with trusted service providers (such as scheduling, email, and analytics vendors) who process it on our behalf, or where required by law.",
        ],
      },
      {
        h: "4. Cookies",
        p: [
          "We use cookies to operate the site and understand how it is used. You can control cookies through your browser settings; disabling them may affect site functionality.",
        ],
      },
      {
        h: "5. Data Security & Retention",
        p: [
          "We use reasonable safeguards to protect your information and retain it only as long as necessary for the purposes described or as required by law.",
        ],
      },
      {
        h: "6. Your Rights & Contact",
        p: [
          `Depending on your location, you may have rights to access, correct, or delete your personal information. To exercise these rights or ask a question, contact ${CONTACT}.`,
        ],
      },
    ],
  },

  earnings: {
    title: "Earnings Claims Statement",
    updated: UPDATED,
    intro: `This Earnings Claims Statement is provided in the interest of full transparency and in accordance with U.S. Federal Trade Commission (FTC) guidance. Please read it carefully before making any decision.`,
    sections: [
      {
        h: "No Guarantee of Income or Results",
        p: [
          `${COMPANY} makes no guarantee, representation, or warranty regarding the amount of sales, revenue, income, or profit you will earn. Operating an eCommerce store carries risk, and many participants will not achieve the results referenced on our website or in our videos.`,
        ],
      },
      {
        h: "Results Are Not Typical",
        p: [
          "Any testimonials, case studies, screenshots, or figures presented (including videos on our YouTube channel) reflect the experiences of specific individual clients. They are illustrative examples and are NOT a representation of typical results. The average client's results may be materially lower, and some clients make little or no profit.",
        ],
      },
      {
        h: "Factors That Affect Your Results",
        p: [
          "Your outcomes depend on many factors, including the capital you invest, the products and categories chosen, competition, seasonality, marketplace fees and policy changes, your account health, economic conditions, and the time and effort involved. Past performance of any store is not indicative of future results.",
        ],
      },
      {
        h: "Forward-Looking Statements",
        p: [
          "Any forward-looking statements about potential performance are estimates only and are inherently uncertain. Actual results will vary and may differ significantly from any examples shown.",
        ],
      },
      {
        h: "Do Your Own Due Diligence",
        p: [
          `You should conduct your own due diligence and consult your own financial, legal, and tax advisors before investing in or engaging any store-management service. By using this site, you acknowledge that ${COMPANY} is not responsible for decisions you make based on the information provided. Questions? Contact ${CONTACT}.`,
        ],
      },
    ],
  },
};
